import type { Theme } from "@mui/material/styles";

/** Inner padding for dashboard cards (MUI spacing units → 24px at default theme). */
export const DASHBOARD_CARD_PADDING = 3;

/** Shared palette and styles for the facility dashboard. */
export const DASHBOARD = {
  /** Neutral page shell behind network dashboard cards (mock --shell-bg). */
  shellBg: "#f7f9fb",
  /** Tinted cards (top metrics + right column) — matches sidebar / mock. */
  tintedCardBg: "#F0F9FA",
  pageBg: "#ffffff",
  cardBg: "#ffffff",
  surfaceMuted: "#f9fafb",
  border: "#D1EAEB",
  borderSubtle: "#D1EAEB",
  header: "#2C5259",
  text: "#5F7D81",
  textMuted: "#5F7D81",
  textDetail: "#6B9096",
  accent: "#065B70",
  accentBar: "#34788c",
  /** Primary metric values (top tiles, historic penalties, trend chart). */
  metricAccent: "#3D8FA3",
  value: "#2C5259",
  iconButtonBg: "#e8f7fa",
  listItemBg: "#ffffff",
} as const;

/** Base card chrome; always pass through theme.spacing for padding (styled() does not treat numbers like sx). */
export function getDashboardCardBaseStyles(theme: Theme) {
  return {
    border: `1px solid ${DASHBOARD.border}`,
    borderRadius: "12px",
    padding: theme.spacing(DASHBOARD_CARD_PADDING),
    boxShadow: "none",
    boxSizing: "border-box" as const,
  };
}

/** For `sx` props — use `p` so MUI resolves spacing correctly. */
export const dashboardCardSx = {
  border: `1px solid ${DASHBOARD.border}`,
  borderRadius: "12px",
  p: DASHBOARD_CARD_PADDING,
  boxSizing: "border-box",
  bgcolor: DASHBOARD.cardBg,
  boxShadow: "none",
} as const;

export function getDashboardTintedCardStyles(theme: Theme) {
  return {
    ...getDashboardCardBaseStyles(theme),
    backgroundColor: DASHBOARD.tintedCardBg,
  };
}

export function getDashboardWhiteCardStyles(theme: Theme) {
  return {
    ...getDashboardCardBaseStyles(theme),
    backgroundColor: DASHBOARD.cardBg,
  };
}

/** @deprecated Prefer getDashboardTintedCardStyles in styled() */
export const dashboardTintedCardSx = {
  ...dashboardCardSx,
  bgcolor: DASHBOARD.tintedCardBg,
} as const;

/** @deprecated Use dashboardTintedCardSx */
export const dashboardMetricCardSx = dashboardTintedCardSx;

/** Gap between dashboard cards (theme spacing units). */
export const DASHBOARD_CARD_GAP = 3;

export const dashboardListItemSx = {
  border: `1px solid ${DASHBOARD.borderSubtle}`,
  borderRadius: "8px",
  p: 2,
  mb: 1.5,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  backgroundColor: DASHBOARD.listItemBg,
} as const;

/** Max list height for document expiry (~4 rows); scroll when there are more items. */
export const DOCUMENT_EXPIRY_LIST_MAX_HEIGHT = "340px";
export const DOCUMENT_EXPIRY_SCROLL_THRESHOLD = 4;

export const dashboardIconButtonSx = {
  width: 32,
  height: 32,
  border: `1px solid ${DASHBOARD.border}`,
  borderRadius: "50%",
  bgcolor: DASHBOARD.iconButtonBg,
  color: DASHBOARD.accent,
  flexShrink: 0,
  "&:hover": { bgcolor: "#dff3f7" },
} as const;

export function dashboardBadgeSx(tone: string) {
  if (tone === "red" || tone === "expired") {
    return { bgcolor: "#fecaca", color: "#991b1b", border: "1px solid #fca5a5", fontWeight: 600 };
  }
  if (tone === "amber") {
    return { bgcolor: "#fde68a", color: "#92400e", border: "1px solid #fcd34d", fontWeight: 600 };
  }
  return {
    bgcolor: "#E0F2FE",
    color: "#1E40AF",
    border: "1px solid #BAE6FD",
    fontWeight: 600,
  };
}

export function dashboardImpactChipSx(level: string) {
  if (level === "affects_policy") {
    return { bgcolor: "#fecaca", color: "#991b1b", border: "none", fontWeight: 600 };
  }
  if (level === "review_suggested") {
    return { bgcolor: "#fde68a", color: "#92400e", border: "none", fontWeight: 600 };
  }
  return {
    bgcolor: "#fff",
    color: DASHBOARD.text,
    border: `1px solid ${DASHBOARD.borderSubtle}`,
    fontWeight: 500,
  };
}

export const dashboardAccordionSx = {
  bgcolor: "transparent",
  boxShadow: "none",
  border: "none",
  "&:before": { display: "none" },
  "&.Mui-expanded": { margin: 0 },
} as const;

export const dashboardAccordionSummarySx = {
  px: 0,
  minHeight: 52,
  "& .MuiAccordionSummary-content": { my: 1, alignItems: "flex-start" },
  "& .MuiAccordionSummary-expandIconWrapper": { color: DASHBOARD.header },
} as const;

/** AccordionSummary default root is a `<button>`; use `div` when the header holds links or info controls. */
export const dashboardAccordionSummaryRootProps = {
  component: "div" as const,
};
